// One lesson as the teacher sees it (SPEC §7.9): time, group, course, room, labels, note, colleague contacts.

import type { MyLesson } from "../../api/types";
import { t, translateCode } from "../../i18n/i18n";
import { formatCell, formatRange } from "../../time";
import { createBadge, createElement } from "../../ui/dom";
import { buildContacts } from "../lesson/candidatesTable";

export function buildMyLessonCard(lesson: MyLesson): HTMLElement {
  const card: HTMLDivElement = createElement("div", `schedule-lesson ${describeStateClass(lesson)}`);
  card.append(createElement("div", "schedule-title", `${formatRange(lesson.range)} ${lesson.groupName}`));
  card.append(createElement("div", "muted", `${lesson.courseName} · ${lesson.classroomName}, ${lesson.centerName}`));
  for (const label of describeLabels(lesson)) {
    card.append(createBadge(label, "neutral"));
  }

  if (lesson.teacherNote !== null) {
    card.append(createElement("p", "", t("mySchedule.noteForYou", { note: lesson.teacherNote })));
  }

  if (lesson.colleague !== null) {
    card.append(buildContacts(lesson.colleague));
  }

  return card;
}

/** Text labels; status is never shown by colour alone (SPEC §9.2). */
export function describeLabels(lesson: MyLesson): string[] {
  const labels: string[] = [];
  const colleague: string = lesson.colleague?.name ?? "";
  if (lesson.involvement === "Substituting") {
    labels.push(t("mySchedule.substitutingFor", { name: colleague }));
  }

  if (lesson.involvement === "CoveredByOther") {
    labels.push(t("mySchedule.coveredBy", { name: colleague }));
  }

  if (lesson.isCancelled) {
    labels.push(t("lessonFlags.cancelled"));
  }

  if (lesson.isCenterClosed && lesson.closureReason !== null) {
    labels.push(t("day.closed", { reason: translateCode("closureReasons", lesson.closureReason) }));
  }

  if (lesson.isMoved) {
    labels.push(t("lesson.movedFrom", { time: formatCell(lesson.plannedRange.start) }));
  }

  if (lesson.isRoomChanged) {
    labels.push(t("lessonFlags.roomChanged"));
  }

  return labels;
}

export function describeStateClass(lesson: MyLesson): string {
  if (lesson.isCancelled || lesson.isCenterClosed) {
    return "is-cancelled";
  }

  return lesson.involvement === "CoveredByOther" ? "is-covered" : "";
}
